import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import {
  FIRST_SIGNUP_WIZARD_STEP,
  ISignUpAgencyForm,
  ISignUpUserForm,
  IUser,
  IUserSignupFormData,
  IUserSignupFormDataErrors,
  LAST_SIGNUP_WIZARD_STEP,
  SIGNUP_STATUS,
  SIGNUP_WIZARD_STEPS,
  USER_ALREADY_EXISTS_ERROR
} from "user/model"
import { userRPO } from "user/repository"
import { userActionCreator } from "user/store/actions"
import { isEmailValid, isValidPassword } from "./common"

export const validateSignupUserFormData = (data: IUserSignupFormData): IUserSignupFormDataErrors => {
  const errors: IUserSignupFormDataErrors = {}
  if (!data.email) {
    errors.email = translate("label.field.required")
  } else if (!isEmailValid(data.email)) {
    errors.email = translate("label.field.invalid.value")
  }
  if (!data.firstname) {
    errors.firstname = translate("label.field.required")
  }
  if (!data.lastname) {
    errors.lastname = translate("label.field.required")
  }
  if (!data.phone || data.phone.length < 4) {
    errors.phone = translate("label.field.required")
  }
  if (!data.password1) {
    errors.password1 = translate("label.field.required")
  } else if (!isValidPassword(data.password1)) {
    errors.password1 = translate("user.password.error.invalid")
  }
  if (!data.password2) {
    errors.password2 = translate("label.field.required")
  } else if (data.password1 !== data.password2) {
    errors.password2 = translate("user.password.error.different")
  }
  return errors
}

export const clearSignUpWizard = (): void => {
  getStore().dispatch(userActionCreator.clearSignUp(getStore().getState().language.locale))
}

export const clearSignUpWizardStatus = (): void => {
  getStore().dispatch(userActionCreator.clearSignUpStatus())
}

export const goToSignUpStep = (step: SIGNUP_WIZARD_STEPS): void => {
  if (!canGoToSignupStep(step)) {
    console.error("Unable to go to step", step)
    return
  }
  getStore().dispatch(userActionCreator.setSignUpStep(step))
}

export const incrementSignUpStep = (): void => {
  goToSignUpStep(getStore().getState().user.signup.step + 1)
}

export const decrementSignUpStep = (): void => {
  goToSignUpStep(getStore().getState().user.signup.step - 1)
}

export const isSignUpPreviousStepEnabled = (step?: SIGNUP_WIZARD_STEPS): boolean =>
  (step !== undefined ? step : getStore().getState().user.signup.step) > 0

export const isSignUpNextStepEnabled = (step?: SIGNUP_WIZARD_STEPS): boolean => {
  const currentStep = step !== undefined ? step : getStore().getState().user.signup.step
  if (currentStep === LAST_SIGNUP_WIZARD_STEP) {
    return false
  } else {
    return canGoToSignupStep(currentStep + 1)
  }
}

export const canGoToSignupStep = (step: SIGNUP_WIZARD_STEPS): boolean => {
  if (step > LAST_SIGNUP_WIZARD_STEP) {
    return false
  } else if (step < FIRST_SIGNUP_WIZARD_STEP) {
    return false
  }
  switch (step) {
    case SIGNUP_WIZARD_STEPS.FIRST: {
      return true
    }
    case SIGNUP_WIZARD_STEPS.SECOND: {
      return getStore().getState().user.signup.userForm.isValid
    }
    case SIGNUP_WIZARD_STEPS.THIRD: {
      const { userForm, agencyForm } = getStore().getState().user.signup
      return userForm.isValid && agencyForm.isValid
    }
  }
}

export const setSignUpUserForm = (userForm: ISignUpUserForm) => {
  getStore().dispatch(userActionCreator.setSignUpUserForm(userForm))
}

export const setSignUpAgencyForm = (agencyForm: ISignUpAgencyForm) => {
  getStore().dispatch(userActionCreator.setSignUpAgencyForm(agencyForm))
}

export const createSignUpUser = async (recaptchaToken: string): Promise<void> => {
  const { agencyForm, userForm } = getStore().getState().user.signup
  if (!agencyForm.isValid || !userForm.isValid) {
    console.error("Unable to create new user, user or agency form is not valid")
    getStore().dispatch(userActionCreator.setSignUpStatus(SIGNUP_STATUS.ERROR))
    return
  }
  getStore().dispatch(userActionCreator.setSignUpStatus(SIGNUP_STATUS.REQUESTING))
  try {
    await userRPO.createAdstandingUser(userForm.data, agencyForm.data, recaptchaToken)
    getStore().dispatch(userActionCreator.setSignUpStatus(SIGNUP_STATUS.SUCCESS))
  } catch (error) {
    console.error(error)
    getStore().dispatch(
      userActionCreator.setSignUpStatus(
        error === USER_ALREADY_EXISTS_ERROR ? SIGNUP_STATUS.ERROR_ALREADY_EXIST : SIGNUP_STATUS.ERROR
      )
    )
  }
}

export const activateAdstandingAccount = async (email: string, code: string): Promise<IUser> => {
  return await userRPO.confirmAdstandingUserEmail(email, code)
}
